import sub from 'date-fns/sub';
import startOfDay from 'date-fns/startOfDay';
import type { Ari } from '@atlassian/jira-platform-ari/src/index.tsx';
import { JiraIssueAri } from '@atlassian/ari/jira/issue';
import type { preloadedDevSummaryDataQuery$data as preloadedDevSummaryDataQueryData } from '@atlassian/jira-relay/src/__generated__/preloadedDevSummaryDataQuery.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import type { rovoDevAgentsUserCheckQuery$data as rovoDevAgentsUserCheckQueryData } from '@atlassian/jira-relay/src/__generated__/rovoDevAgentsUserCheckQuery.graphql';
import { preloadedDevSummaryData } from './dev-panel-requests/preloadedDevSummaryData.tsx';
import { rovoDevAgentsUserCheck } from './dev-panel-requests/rovoDevAgentsUserCheck.tsx';
import { fetchCreateBranchTargets } from './dev-panel-requests/branch-target.tsx';
import type { RemoteAppResponse } from './dev-panel-requests/types.tsx';
import { getHasDevelopmentDataInProject } from './dev-panel-requests/has-development-in-project.tsx';

type FetchDevSummaryDataType = (
	issueAri: Ari,
) => Promise<preloadedDevSummaryDataQueryData | undefined>;

class PreloadedDataRequests {
	currentIssueAri?: Ari;

	fetchDevSummaryPromises: Map<Ari, Promise<preloadedDevSummaryDataQueryData | undefined>> =
		new Map();

	rovoDevAgentsUserCheckPromises: Map<Ari, Promise<rovoDevAgentsUserCheckQueryData | undefined>> =
		new Map();

	fetchCreateBranchTargetsPromises: Map<string, Promise<RemoteAppResponse>> = new Map();

	hasDevelopmentInProjectPromises: Map<string, Promise<boolean>> = new Map();

	clearAll() {
		this.fetchDevSummaryPromises.clear();
		this.fetchCreateBranchTargetsPromises.clear();
		this.hasDevelopmentInProjectPromises.clear();
		this.rovoDevAgentsUserCheckPromises.clear();
	}

	getIssueAri() {
		return this.currentIssueAri;
	}

	setIssueAri(issueAri: Ari) {
		this.currentIssueAri = issueAri;
	}

	fetchAllAGGDependantRequests(
		issueAri: Ari,
		projectAri: string,
		isAdmin: boolean,
		isSiteAdmin: boolean,
		atlassianAccountId: string | null,
	) {
		// !fg('devai-issue-view-main-migration') is the value of skipCheck
		// in src/packages/autodev/jira-autodev-common/src/services/use-is-autodev-enabled/index.tsx
		// from src/packages/development/summary/development-field/src/ui/summary/index.tsx
		const shouldFetchRovoDevAgentsUserCheck =
			!fg('devai-issue-view-main-migration') &&
			atlassianAccountId !== null &&
			atlassianAccountId?.trim() !== '';

		this.getFetchDevSummaryPromise(issueAri);
		if (shouldFetchRovoDevAgentsUserCheck) {
			this.getFetchAutoDevQueryPromise(issueAri, atlassianAccountId);
		}
		this.getFetchCreateBranchTargetsPromise(issueAri);
		this.getHasDevelopmentInProject(issueAri, projectAri, isAdmin, isSiteAdmin);
	}

	getFetchDevSummaryPromise(issueAri: Ari) {
		let fetchDevSummaryPromise = this.fetchDevSummaryPromises.get(issueAri);

		if (fetchDevSummaryPromise) {
			return fetchDevSummaryPromise;
		}

		fetchDevSummaryPromise = preloadedDevSummaryData(issueAri);

		this.fetchDevSummaryPromises.set(issueAri, fetchDevSummaryPromise);

		return fetchDevSummaryPromise;
	}

	getFetchCreateBranchTargetsPromise(issueAri: Ari) {
		let fetchCreateBranchTargetsPromise = this.fetchCreateBranchTargetsPromises.get(issueAri);

		if (fetchCreateBranchTargetsPromise) {
			return fetchCreateBranchTargetsPromise;
		}

		const issue = JiraIssueAri.parse(issueAri);
		fetchCreateBranchTargetsPromise = fetchCreateBranchTargets(issue.issueId);

		this.fetchCreateBranchTargetsPromises.set(issueAri, fetchCreateBranchTargetsPromise);

		return fetchCreateBranchTargetsPromise;
	}

	getFetchAutoDevQueryPromise(issueAri: Ari, atlassianAccountId: string) {
		let rovoDevAgentsUserCheckPromise = this.rovoDevAgentsUserCheckPromises.get(issueAri);
		if (rovoDevAgentsUserCheckPromise) {
			return rovoDevAgentsUserCheckPromise;
		}
		const { siteId } = JiraIssueAri.parse(issueAri);

		rovoDevAgentsUserCheckPromise = rovoDevAgentsUserCheck(
			atlassianAccountId,
			siteId,
			fg('coding-agents-limited-beta'),
		);
		this.rovoDevAgentsUserCheckPromises.set(issueAri, rovoDevAgentsUserCheckPromise);
		return rovoDevAgentsUserCheckPromise;
	}

	getHasDevelopmentInProject(
		issueAri: Ari,
		projectAri: string,
		isJiraAdmin: boolean,
		isSiteAdmin: boolean,
	) {
		let hasDevelopmentInProjectPromise = this.hasDevelopmentInProjectPromises.get(issueAri);

		if (hasDevelopmentInProjectPromise) {
			return hasDevelopmentInProjectPromise;
		}

		const updatedFrom = this.getUpdatedFromDate();

		hasDevelopmentInProjectPromise = getHasDevelopmentDataInProject(
			projectAri,
			updatedFrom,
			isJiraAdmin,
			isSiteAdmin,
		);

		this.hasDevelopmentInProjectPromises.set(issueAri, hasDevelopmentInProjectPromise);

		return hasDevelopmentInProjectPromise;
	}

	private getUpdatedFromDate() {
		return startOfDay(
			sub(new Date(), {
				weeks: 6,
			}),
		).toISOString();
	}
}

/**
 * Singleton instance to manage preloaded data requests for issue view.
 */
export const preloadedDataRequests: PreloadedDataRequests = new PreloadedDataRequests();

export const fetchDevSummaryData: FetchDevSummaryDataType = (issueAri) => {
	return preloadedDataRequests.getFetchDevSummaryPromise(issueAri);
};
